import { useMemo, useState } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { usePreviewStore } from '@/stores/preview-store'
import { useAuthStore } from '@/stores/auth-store'
import { formatFileSize } from '@/lib/schemas'
import { getExpiryStatus } from '@/lib/document-expiry'
import { Button } from '@/components/ui/button'
import { CreateFolderDialog } from '@/components/CreateFolderDialog'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { FileText, FolderPlus, Upload, Sparkles, Shield, MoreHorizontal } from 'lucide-react'
import { TagChip, TagOverflowChip } from '@/components/ui/TagChip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface LibraryOverviewProps {
  /** Opens the system file picker. Hooked up from LibraryPage's lifted dropzone. */
  onUploadClick?: () => void
}

function formatRelativeTime(dateString: string): string {
  const now = Date.now()
  const date = new Date(dateString).getTime()
  const diffMs = now - date
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 30) return `${diffDays}d ago`
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
  })
}

export function LibraryOverview({ onUploadClick }: LibraryOverviewProps) {
  const { documents, folders, getRecentDocuments, setQualityTier, setFactBank, setExpiringFilter } =
    useLibraryStore()
  const isViewer = useAuthStore((s) => s.role === 'viewer')

  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false)

  const recentDocuments = getRecentDocuments(10)

  const stats = useMemo(() => {
    const totalSize = documents.reduce((sum, doc) => sum + (doc.size || 0), 0)
    return {
      documentCount: documents.length,
      totalSize,
      folderCount: folders.length,
    }
  }, [documents, folders])

  // Documents within the 30-day soon-window (not yet expired) — the renew-before-it-lapses
  // nudge. Already-expired docs carry their own loud pills in the lists.
  // Surfaced as a flat link so the user can jump to the all-documents view.
  const expiringSoonCount = useMemo(
    () => documents.filter((doc) => getExpiryStatus(doc, new Date()) === 'expiring-soon').length,
    [documents]
  )

  const folderNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const folder of folders) {
      map.set(folder.id, folder.name)
    }
    return map
  }, [folders])

  // First-time state: zero folders AND zero docs. Show a friendlier welcome
  // with both primary CTAs side-by-side instead of the slim stats row + a
  // single "library is empty" message.
  const isFirstTime = folders.length === 0 && documents.length === 0

  if (isFirstTime) {
    return (
      <>
        <FirstTimeWelcome
          isViewer={isViewer}
          onCreateFolder={() => setIsCreateFolderOpen(true)}
          {...(onUploadClick && { onUploadClick })}
        />
        <CreateFolderDialog
          open={isCreateFolderOpen}
          onOpenChange={setIsCreateFolderOpen}
          defaultParentId={null}
        />
      </>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-auto">
      {/* Header — slim stats line. "New Folder" lives in the toolbar (LibraryToolbar);
          no duplicate action or page title here. */}
      <div className="px-6 py-3 border-b border-border">
        <p className="text-meta">
          {stats.documentCount} document{stats.documentCount !== 1 ? 's' : ''}
          {' · '}
          {formatFileSize(stats.totalSize)}
          {' · '}
          {stats.folderCount} folder{stats.folderCount !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Expiring-soon flat line — only when at least one document qualifies.
          Click applies the expiring lens (folder stays null) which switches the
          panel to the filtered document list — the attention set (expiring-soon
          OR expired). The count here is soon-only; the filter also shows expired. */}
      {expiringSoonCount > 0 && (
        <button
          type="button"
          onClick={() => setExpiringFilter(true)}
          className="flex items-center gap-1.5 px-6 py-2 text-xs text-[hsl(var(--link))] hover:underline"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-warning))]" />
          Expiring soon: {expiringSoonCount} document{expiringSoonCount !== 1 ? 's' : ''}
        </button>
      )}

      <div className="px-6 py-4 space-y-6">
        {/* Recent Uploads */}
        {recentDocuments.length > 0 && (
          <section>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              Recent Uploads
            </h3>
            <div className="flex items-center gap-3 px-3 py-1 text-meta border-b">
              <span className="w-7 flex-shrink-0" aria-hidden />
              <span className="flex-1 min-w-0">Name</span>
              <span className="flex-shrink-0">Uploaded by</span>
              <span className="flex-shrink-0 w-14 text-right">Time</span>
              {/* Spacer matching the row's trailing actions (shield + kebab, 24px each + gap-3) */}
              {!isViewer && <span className="w-[60px] flex-shrink-0" aria-hidden />}
            </div>
            {recentDocuments.map((doc) => {
              const inFactBank = doc.inFactBank === true
              const isArchived = doc.qualityTier === 'archive'
              const isExpired = getExpiryStatus(doc, new Date()) === 'expired'
              return (
              <div
                key={doc.id}
                className={cn(
                  'group w-full flex items-center gap-3 px-3 py-2 text-left',
                  'border-b last:border-b-0 transition-colors',
                  isArchived ? 'opacity-55 hover:bg-muted/30' : 'hover:bg-muted/50'
                )}
              >
                <button
                  type="button"
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  onClick={() =>
                    void usePreviewStore.getState().openPreview({
                      documentId: doc.id,
                      documentName: doc.name,
                      context: 'library',
                    })
                  }
                >
                  <FileTypeIcon mimeType={doc.mimeType} size={28} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{doc.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {doc.folderId && (
                        <p className="text-meta truncate">
                          {folderNameMap.get(doc.folderId) ?? ''}
                        </p>
                      )}
                      {isArchived && (
                        <>
                          {doc.folderId && <span className="text-xs text-subtle">·</span>}
                          <span
                            className="inline-flex items-center h-[18px] px-[7px] rounded-[5px] text-[10px] font-semibold uppercase tracking-[0.04em] bg-muted border border-border text-muted-foreground"
                          >
                            Archived
                          </span>
                        </>
                      )}
                      {isExpired && (
                        <span
                          className="inline-flex items-center h-[18px] px-[7px] rounded-[5px] text-[10px] font-semibold uppercase tracking-[0.04em] bg-destructive/10 text-destructive"
                        >
                          Expired
                        </span>
                      )}
                      {!isArchived && doc.tags && doc.tags.length > 0 && (
                        <>
                          {doc.folderId && (
                            <span className="text-xs text-subtle">·</span>
                          )}
                          <div className="flex items-center gap-1 min-w-0">
                            {doc.tags.slice(0, 3).map((tag) => (
                              <TagChip key={tag.id} name={tag.name} color={tag.color} />
                            ))}
                            {doc.tags.length > 3 && (
                              <TagOverflowChip count={doc.tags.length - 3} />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="text-meta flex-shrink-0">
                    {doc.uploaderName ?? ''}
                  </span>
                  <span className="text-meta flex-shrink-0 w-14 text-right">
                    {formatRelativeTime(doc.createdAt)}
                  </span>
                </button>

                {/* Fact Bank action — persistent shield when a member; hover-reveal otherwise */}
                {!isViewer && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      void setFactBank(doc.id, !inFactBank)
                    }}
                    title={inFactBank
                      ? 'In the Fact Bank — the AI answers factual questions from this document'
                      : 'Add to Fact Bank'}
                    aria-label={inFactBank ? 'Remove from Fact Bank' : 'Add to Fact Bank'}
                    className={cn(
                      'w-6 h-6 inline-flex items-center justify-center rounded-md',
                      'hover:bg-accent transition-colors',
                      inFactBank
                        ? 'text-[hsl(var(--brand))]'
                        : 'opacity-0 group-hover:opacity-100 text-muted-foreground'
                    )}
                  >
                    <Shield
                      className={cn('h-3.5 w-3.5', inFactBank && 'fill-[hsl(var(--brand))]')}
                    />
                  </button>
                )}

                {/* Kebab menu — hover-reveal */}
                {!isViewer && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        title="More actions"
                        aria-label="More actions"
                        className="w-6 h-6 inline-flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-colors"
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        onClick={() => void setFactBank(doc.id, !inFactBank)}
                      >
                        <Shield className={cn('h-3.5 w-3.5 mr-2', inFactBank && 'fill-[hsl(var(--brand))] text-[hsl(var(--brand))]')} />
                        {inFactBank ? 'Remove from Fact Bank' : 'Add to Fact Bank'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => void setQualityTier(doc.id, isArchived ? null : 'archive')}
                      >
                        {isArchived ? 'Unarchive' : 'Archive'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )})}
          </section>
        )}

        {/* "Folders exist but no documents" fallback */}
        {documents.length === 0 && folders.length > 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="text-sm font-medium mb-1">No documents yet</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              You have folders set up, but no documents have been uploaded yet.
            </p>
            {!isViewer && onUploadClick && (
              <Button size="sm" className="mt-4" onClick={onUploadClick}>
                <Upload className="h-4 w-4 mr-2" />
                Upload your first document
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// First-time welcome — shown when the org has zero folders and zero documents
// ============================================================================

function FirstTimeWelcome({
  isViewer,
  onCreateFolder,
  onUploadClick,
}: {
  isViewer: boolean
  onCreateFolder: () => void
  onUploadClick?: () => void
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="inline-flex items-center justify-center mb-5 text-[hsl(var(--brand))]">
          <Sparkles className="h-10 w-10" />
        </div>
        <h2 className="text-lg font-semibold mb-2">Welcome to your library</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Your library is where the AI looks for evidence when drafting bid
          responses. Add your past proposals, capability statements, case
          studies, and policies — organised in folders.
        </p>

        {isViewer ? (
          <p className="text-meta italic">
            Ask your admin to start populating the library.
          </p>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button onClick={onCreateFolder}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Create your first folder
            </Button>
            {onUploadClick && (
              <Button variant="outline" onClick={onUploadClick}>
                <Upload className="h-4 w-4 mr-2" />
                Upload a document
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
